import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { CLASS_LIST, CLASSES } from '../game/classes';
import { ASCENSIONS } from '../game/ascensions';
import { curseById } from '../game/curses';
import { relicById } from '../game/relics';
import GameCard from '../components/GameCard';
import { ArrowLeft, Play, Skull, ChevronLeft, ChevronRight } from 'lucide-react';

export default function ClassSelect() {
  const { pendingClassChoice, confirmClass, goToMenu, profile, activeCurse } = useGame();
  const [hover, setHover] = useState(CLASS_LIST[0]);
  const [asc, setAsc] = useState(0);
  const [useAlt, setUseAlt] = useState(false);
  const cls = CLASSES[hover];
  const relic = cls && relicById(cls.startingRelic);
  const maxUnlock = profile?.ascension_unlocks?.[hover] ?? 0;
  const mastery = profile?.masteries?.[hover] ?? 0;
  const altUnlocked = mastery >= 3 && !!cls?.altStarterDeck;

  useEffect(() => {
    setAsc(a => Math.min(a, maxUnlock));
    setUseAlt(false);
  }, [hover, maxUnlock]);

  const curseInfo = activeCurse ? curseById(activeCurse.curse_id) : null;

  return (
    <div className="min-h-screen w-full px-6 py-6">
      <div className="flex items-center justify-between">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="classselect-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">SEED · <span className="text-[color:var(--pink)]">{pendingClassChoice?.seed}</span>{pendingClassChoice?.daily && ' · DAILY'}</div>
        <div className="w-16" />
      </div>

      <h1 className="mt-4 font-display text-5xl sm:text-6xl font-black uppercase text-[color:var(--neon)] text-center">CHOOSE YOUR SLOP</h1>

      {curseInfo && (
        <div className="mt-6 mx-auto max-w-3xl brutal-border-pink p-4 bg-[#160a10] flex items-center gap-4" data-testid="active-curse-banner">
          <Skull className="text-[color:var(--red)]" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[color:var(--pink)]">CURSED BY {activeCurse.cursed_by}</div>
            <div className="font-display font-black text-2xl uppercase" style={{color: curseInfo.color}}>{curseInfo.name}</div>
            <div className="text-xs text-[color:var(--text-dim)]">{curseInfo.desc}</div>
          </div>
          <div className="text-right text-xs uppercase font-bold text-[color:var(--pink)]">2x TOKENS ON WIN</div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {CLASS_LIST.map(id => {
          const c = CLASSES[id];
          const isHover = hover === id;
          return (
            <div
              key={id}
              onMouseEnter={() => setHover(id)}
              onClick={() => confirmClass(id, asc)}
              className="p-6 bg-[#0a0a0a] cursor-pointer transition-all hover:-translate-y-2 border-2"
              style={{ borderColor: isHover ? c.color : 'var(--muted)', boxShadow: isHover ? `6px 6px 0 ${c.color}` : '6px 6px 0 var(--muted)' }}
              data-testid={`class-card-${id}`}
            >
              <div className="text-6xl">{c.icon}</div>
              <div className="mt-4 font-display font-black text-2xl uppercase" style={{ color: c.color }}>{c.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] mt-1">{c.tag}</div>
              <div className="mt-4 text-sm text-[color:var(--text)]">{c.desc}</div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="brutal-border-dim px-2 py-1">
                  <span className="text-[color:var(--text-dim)]">HP </span>
                  <span className="font-bold text-white">{c.startingHp}</span>
                </div>
                <div className="brutal-border-dim px-2 py-1">
                  <span className="text-[color:var(--text-dim)]">MAX ASC </span>
                  <span className="font-bold" style={{color: c.color}}>A{profile?.ascension_unlocks?.[id] ?? 0}</span>
                </div>
              </div>
              <button className="brutal-btn text-sm w-full mt-4 flex items-center justify-center gap-2"
                onClick={(e) => { e.stopPropagation(); confirmClass(id, asc, useAlt && id === hover); }}
                style={{ borderColor: c.color, color: c.color, boxShadow: `4px 4px 0 ${c.color}` }}
                data-testid={`class-choose-${id}`}>
                <Play size={14}/> DROP IN · A{asc}
              </button>
            </div>
          );
        })}
      </div>

      {/* Ascension picker */}
      <div className="mt-8 max-w-5xl mx-auto brutal-border p-4 bg-[#0a0a0a]" data-testid="ascension-picker">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">ASCENSION LEVEL — {cls.name}</div>
          <div className="text-xs uppercase text-[color:var(--pink)]">UNLOCKED: A{maxUnlock}/5</div>
        </div>
        <div className="flex items-center gap-3">
          <button className="brutal-btn text-xs px-2 py-1" onClick={() => setAsc(a => Math.max(0, a - 1))} data-testid="asc-decrement">
            <ChevronLeft size={16} />
          </button>
          <div className="flex-1 flex gap-1">
            {ASCENSIONS.map((a) => (
              <div
                key={a.level}
                className={`flex-1 py-3 text-center border-2 cursor-pointer transition-all ${a.level === asc ? 'bg-[color:var(--neon)] text-black border-[color:var(--neon)]' : a.level <= maxUnlock ? 'border-[color:var(--muted)] text-white hover:border-[color:var(--neon)]' : 'border-[color:var(--muted)] text-[color:var(--muted)] cursor-not-allowed'}`}
                onClick={() => a.level <= maxUnlock && setAsc(a.level)}
                data-testid={`asc-level-${a.level}`}
              >
                <div className="font-display font-black">A{a.level}</div>
                <div className="text-[9px] uppercase tracking-widest">{a.name}</div>
              </div>
            ))}
          </div>
          <button className="brutal-btn text-xs px-2 py-1" onClick={() => setAsc(a => Math.min(maxUnlock, a + 1))} data-testid="asc-increment">
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="mt-2 text-xs text-[color:var(--text-dim)]" data-testid="asc-current-desc">
          <span className="text-[color:var(--pink)]">A{asc} — {ASCENSIONS[asc].name}:</span> {ASCENSIONS[asc].desc}
        </div>
      </div>

      {cls && (
        <div className="mt-8 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
              STARTER DECK — {cls.name}
              {altUnlocked && <span className="ml-2 text-[color:var(--pink)]">· MASTERY {mastery}</span>}
            </div>
            {altUnlocked && (
              <button
                className={`brutal-btn text-xs ${useAlt ? 'brutal-btn-pink' : ''}`}
                onClick={() => setUseAlt(v => !v)}
                data-testid="alt-starter-toggle"
              >
                {useAlt ? 'USING ALT DECK' : 'USE ALT DECK'}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {(useAlt && cls.altStarterDeck ? cls.altStarterDeck : cls.starterDeck).map((cid, i) => (
              <GameCard key={i} cardId={cid} small testid={`starter-card-${cid}-${i}`} />
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">STARTER RELIC:</div>
            <div className="brutal-border-pink px-3 py-2 flex items-center gap-3">
              <span className="text-2xl">{relic.art}</span>
              <div>
                <div className="font-display font-bold text-[color:var(--pink)]">{relic.name}</div>
                <div className="text-xs text-[color:var(--text-dim)]">{relic.desc}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
